import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react"; // Added X import for close icon
import { useNavigate } from "react-router-dom";
import "../css/Navbar.css";

interface NavbarProps {
    scrollToSection: (sectionId: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ scrollToSection }) => {
    const [scrolled, setScrolled] = useState<boolean>(false);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [overHero, setOverHero] = useState<boolean>(false);
    const [scrollPosition, setScrollPosition] = useState<number>(0);

    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            const currentScroll = window.scrollY;
            setScrollPosition(currentScroll);
            setScrolled(currentScroll > 50);

            // Close menu when scrolling
            if (menuOpen && currentScroll > scrollPosition) {
                setMenuOpen(false);
            }

            const heroSection = document.getElementById("hero");
            if (heroSection) {
                const heroRect = heroSection.getBoundingClientRect();
                const navbarHeight = 80;
                setOverHero(heroRect.top <= navbarHeight && heroRect.bottom >= navbarHeight);
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, [menuOpen, scrollPosition]); // Added dependencies

    const shouldChangeColor = overHero && scrollPosition > 100;
    const navLinkColor = shouldChangeColor ? "#68F8FD" : "#01494B";

    const handleNavClick = (path: string, sectionId?: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        if (sectionId) {
            navigate(`/#${sectionId}`);
            scrollToSection(sectionId);
        } else {
            navigate(path);
        }
        setMenuOpen(false);
    };

    return (
        <nav className={`navbar ${scrolled ? "scrolled" : ""} ${menuOpen ? "menu-open" : ""}`}>
            <div className="nav-container">
                <div className="menu-icon" onClick={() => setMenuOpen(!menuOpen)}>
                    {menuOpen ? (
                        <X size={30} color={navLinkColor} />
                    ) : (
                        <Menu size={30} color={navLinkColor} />
                    )}
                </div>
                <div className="logo"></div>

                <ul className="nav-links">
                    {/* Desktop nav links remain unchanged */}
                    <li>
                        <a style={{ color: navLinkColor }} onClick={handleNavClick("/#about", "about")} href="#about">
                            About Us
                        </a>
                    </li>
                    <li>
                        <a
                            style={{ color: navLinkColor }}
                            onClick={handleNavClick("/#board-ofdirectors", "board-ofdirectors")}
                            href="#board-ofdirectors"
                        >
                            Our Directors
                        </a>
                    </li>
                    <li>
                        <a
                            style={{ color: navLinkColor }}
                            onClick={handleNavClick("/#subsidiaries", "subsidiaries")}
                            href="#subsidiaries"
                        >
                            Subsidiaries
                        </a>
                    </li>
                    <li>
                        <a style={{ color: navLinkColor }} 
                        // onClick={handleNavClick("/contact")} 
                        // href="/contact"
                        >
                            Contact
                        </a>
                    </li>
                </ul>
            </div>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="mobile-menu"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <ul>
                            <li>
                                <a
                                    style={{ color: navLinkColor }}
                                    onClick={handleNavClick("/#about", "about")}
                                    href="#about"
                                >
                                    About Us
                                </a>
                            </li>
                            <li>
                                <a
                                    style={{ color: navLinkColor }}
                                    onClick={handleNavClick("/#board-ofdirectors", "board-ofdirectors")}
                                    href="#board-ofdirectors"
                                >
                                    Our Director
                                </a>
                            </li>
                            <li>
                                <a
                                    style={{ color: navLinkColor }}
                                    onClick={handleNavClick("/#subsidiaries", "subsidiaries")}
                                    href="#subsidiaries"
                                >
                                    Subsidiary
                                </a>
                            </li>
                            <li>
                                <a style={{ color: navLinkColor }} onClick={handleNavClick("/contact")} href="/contact">
                                    Contact
                                </a>
                            </li>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;